// extensionDataSeed.js — pure live-data seed for AI-drawn answer views saved as
// extensions. Kept dependency-free (no smbls / no iframe / no DOM) so it's unit-
// testable under `node --test` and can't drag browser-only deps into a test run.
//
// WHY THIS EXISTS
// A view Simone DRAWS as a live answer (render_answer_ui) reads its data from the
// workspace root — `s.root.<stateKey>`. In the split pane that resolves because
// the view mounts on the LIVE workspace root. But a SAVED extension renders in an
// ISOLATED iframe (createIframeApp) whose root state starts EMPTY (smbls create.js
// gives a root element with no non-empty `state` a plain `{}`), so every
// `s.root.<key>` read comes back undefined and the saved view renders BLANK.
// That silent-failure is the one gap the isolation model opened for the
// render_answer_ui → "Save as app" bridge.
//
// THE SEED
// renderExtensionInPane (functions/index.js) snapshots the exact root keys the
// source references and seeds them into the iframe page def's `state` before
// mount, so a saved answer view renders with the same data it was drawn against —
// instant, no re-fetch (matches "reuse in-memory workspace data").
//
// SAFETY
//   1. Only keys the source LITERALLY references are seeded (referencedRootKeys).
//   2. Credential / auth-claim / org-identity keys are NEVER seeded (deny sets
//      below) — the scoped data bridge (extensionDataBridge) is the only
//      sanctioned channel for anything beyond referenced display collections.
//   3. Values are DEEP-CLONED so the iframe can never mutate live workspace
//      state by reference.
//
// SEMANTICS
// A snapshot re-taken on every render — re-opening a saved view while its data is
// loaded reflects current data. A referenced-but-not-loaded key is seeded as []
// so the dominant collection idiom (`s.root.<key>.map`/`.length`) renders an
// EMPTY view instead of throwing on undefined — honest degradation, not a blank
// pane. A view meant to ALWAYS show fresh data should inline literal rows or
// declare a `fetch:` surface (both cross the iframe boundary intact).

// Root keys never seeded into an extension iframe — credentials, auth claims,
// and org/workspace identity structures.
export const SEED_DENY_KEYS = new Set([
  'jwtClaims',
  'orgs',
  'org',
  'workspaces',
  'workspace',
  'userPrefs',
  'roles',
  'orgMembersByOrgId'
])

// Substring guard for anything credential/session-shaped, regardless of exact
// key name (belt-and-suspenders over the explicit set above).
export const SEED_DENY_RE = /token|secret|jwt|claim|credential|password|apikey|auth|session/i

const isDenied = (key) => SEED_DENY_KEYS.has(key) || SEED_DENY_RE.test(key)

// Root-state keys an AI-drawn source references: `s.root.<key>`, `s.root?.<key>`,
// `state.root.<key>`, and bracket `…root['<key>']` forms. Anchored on `.root` so
// it only matches ROOT reads, never arbitrary property access elsewhere.
export const referencedRootKeys = (source) => {
  const text = String(source || '')
  const keys = new Set()
  const dot = /\.root(?:\?\.|\.)([A-Za-z_$][\w$]*)/g
  const bracket = /\.root(?:\?\.)?\s*\[\s*['"]([A-Za-z_$][\w$]*)['"]\s*\]/g
  let m
  while ((m = dot.exec(text))) keys.add(m[1])
  while ((m = bracket.exec(text))) keys.add(m[1])
  return keys
}

const defaultClone = (v) => {
  try {
    if (typeof structuredClone === 'function') return structuredClone(v)
  } catch (_) {
    /* fall through */
  }
  try {
    return JSON.parse(JSON.stringify(v))
  } catch (_) {
    return v
  }
}

// Build the iframe root seed for a drawn-view source from the live workspace root
// state. `clone` is injectable (renderExtensionInPane passes smbls deepClone for
// fidelity; tests/default use structuredClone). Returns { seed, seeded, missing }:
//   seed    — { key: clonedValue } to spread into the iframe page def's state
//   seeded  — keys that had live data (rendered faithfully)
//   missing — referenced keys with no live data (seeded as [] for graceful empty)
export const buildRootStateSeed = (source, rootState, { clone = defaultClone } = {}) => {
  const refs = referencedRootKeys(source)
  const seed = {}
  const seeded = []
  const missing = []
  for (const key of refs) {
    if (isDenied(key)) continue
    const live = rootState ? rootState[key] : undefined
    if (typeof live === 'function') continue
    if (live === undefined || live === null) {
      seed[key] = []
      missing.push(key)
      continue
    }
    seed[key] = clone(live)
    seeded.push(key)
  }
  return { seed, seeded, missing }
}
